// Browser storage that belongs to the signed-in coach, and what survives a
// sign-out.
//
// Cleared on sign-out: the tab's fulfillment selection (a delivery address
// and its coordinates — see fulfillmentSession.js) and the active-team
// pointer. Both used to outlive the session, so the next person at a shared
// browser started on the previous coach's address. Also the tab's discovery
// and menu caches, whose keys alone carry the delivery address and its
// coordinates (phantomRestaurantSnapshots.js, menuProviderService.js), and the
// share-link identities a signed-in coach confirmed in this tab (the email
// typed at a shared template, poll or cart gate). And the coach's carts'
// quoted prices (quotedItemPrices.js, keyed by cart id) and the ordering mode
// Phantom last reported for them (orderingModeMemory.js, keyed by user id).
// Caches that hold the same data in memory (the DoorDash address book,
// discovery rows, menus, the assistant's restaurant rows, quoted prices, the
// ordering mode) listen for SIGNED_OUT_EVENT and drop theirs.
//
// Kept: each coach's last-used team, under a key of their own, so signing
// back in returns them to the team they were on instead of whichever team a
// query happened to list first. It is only ever read for that same coach, and
// only honoured while they still coach the team.
//
// The same clearing runs when a different coach signs in over this one
// without signing out (SessionContext). The tab remembers whose it is
// (TAB_OWNER_KEY) so a switch that lands on a reload is caught too.

import { clearSessionFulfillment } from './fulfillmentSession';

const ACTIVE_TEAM_KEY = 'activeTeamId';
const TAB_OWNER_KEY = 'mealops:tabOwner';
const SIGNED_OUT_SESSION_PREFIXES = [
  TAB_OWNER_KEY,
  'phantom:restaurant-discovery:',
  'coach:menuCache:',
  'sharedTemplate:gate:',
  'sharedTemplate:activeToken',
  'sharedPoll:identity:',
  'sharedCart:gate:',
  'mealops.quotedPrices.',
];
const SIGNED_OUT_LOCAL_PREFIXES = ['cih:ordering-mode:'];
export const SIGNED_OUT_EVENT = 'mealops:signed-out';
const LAST_TEAM_PREFIX = 'mealops:lastTeam:';

const lastTeamKey = (userId) => `${LAST_TEAM_PREFIX}${userId}`;

const removeKeysWithPrefix = (storage, prefixes) => {
  const keys = [];
  for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
  keys
    .filter((key) => prefixes.some((prefix) => key?.startsWith(prefix)))
    .forEach((key) => storage.removeItem(key));
};

export function clearSignedOutStorage() {
  clearSessionFulfillment();
  try {
    removeKeysWithPrefix(window.sessionStorage, SIGNED_OUT_SESSION_PREFIXES);
  } catch { /* storage unavailable */ }
  try {
    window.localStorage.removeItem(ACTIVE_TEAM_KEY);
    removeKeysWithPrefix(window.localStorage, SIGNED_OUT_LOCAL_PREFIXES);
  } catch { /* storage unavailable */ }
  try {
    window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
  } catch { /* no window (tests) */ }
}

// Whose session this tab last held. Per tab (sessionStorage), like the
// state it guards.
export function rememberTabOwner(userId) {
  if (!userId) return;
  try {
    window.sessionStorage.setItem(TAB_OWNER_KEY, userId);
  } catch { /* storage unavailable */ }
}

export function readTabOwner() {
  try {
    return window.sessionStorage.getItem(TAB_OWNER_KEY) || null;
  } catch {
    return null;
  }
}

export function rememberLastTeam(userId, teamId) {
  if (!userId || !teamId) return;
  try {
    window.localStorage.setItem(lastTeamKey(userId), teamId);
  } catch { /* storage unavailable */ }
}

export function readLastTeam(userId) {
  if (!userId) return null;
  try {
    return window.localStorage.getItem(lastTeamKey(userId)) || null;
  } catch {
    return null;
  }
}
